import { getEnvironmentDatabase } from "client/Database";
import { StateStorage } from "./notPublic";
import { IEnvironmentDatabase } from "client/Database/Environment";
import { IUserDatabaseSession } from "client/Database/Environment/session";
import { ZustandStore } from "client/Database/Environment/models";
import { IRepository } from "client/Database/repository";
import { waitForUserConfigurationToInitialize } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { safeLocalStorage } from "client_legacy/ndaw/safeStorage";
import { Guid } from "client_legacy/ndaw/guid";

export type IndexedDbStateStorageOptions = {
	onAfterSetItem?: (name: string) => Promise<void> | void;
	shouldOnlyPersistDataForCurrentUser?: boolean;
};

export class IndexedDbStateStorage implements StateStorage {
	private readonly _options: IndexedDbStateStorageOptions;
	constructor(options: IndexedDbStateStorageOptions = {}) {
		this._options = options;
	}

	public async getItem(name: string): Promise<string | null> {
		const database: IEnvironmentDatabase = await getEnvironmentDatabase();
		const session: IUserDatabaseSession = await database.openForCurrentUser();

		const repository: IRepository<ZustandStore, string> = this.resolveRepository(session);

		const store: ZustandStore | undefined = await repository.get(name);

		if (!store) {
			return null;
		}

		return store.data;
	}

	public async setItem(name: string, value: string): Promise<void> {
		const {
			onAfterSetItem,
		} = this._options;

		const database: IEnvironmentDatabase = await getEnvironmentDatabase();
		const session: IUserDatabaseSession = await database.openForCurrentUser();

		const repository: IRepository<ZustandStore, string> = this.resolveRepository(session);

		await repository.addOrUpdate({ name: name, data: value });

		if (onAfterSetItem) {
			await onAfterSetItem(name);
		}
	}

	public async checkIfAvailable(): Promise<boolean> {
		const database: IEnvironmentDatabase = await getEnvironmentDatabase();
		try {
			await database.openForCurrentUser();
			return true;
		} catch (e) {
			if (!(e instanceof DOMException)) {
				throw e;
			}

			return false;
		}
	}

	private resolveRepository(session: IUserDatabaseSession): IRepository<ZustandStore, string> {
		const {
			shouldOnlyPersistDataForCurrentUser = false
		} = this._options;

		return shouldOnlyPersistDataForCurrentUser
			? session.singleUserZustandStores
			: session.zustandStores;
	}
}

export class IsolatedLocalStorageStateStorage implements StateStorage {
	public async getItem(name: string): Promise<string> {
		const {
			currentUserIdentity: {
				userId
			},
			databaseConfiguration: {
				environmentId
			}
		} = await waitForUserConfigurationToInitialize();

		const storedItem: string | nil = safeLocalStorage.getItem<string>(this.getLocalStorageKey(name, environmentId, userId));
		if (!storedItem) {
			return "";
		}

		return storedItem;
	}

	public async setItem(name: string, value: string): Promise<void> {
		const {
			currentUserIdentity: {
				userId
			},
			databaseConfiguration: {
				environmentId
			}
		} = await waitForUserConfigurationToInitialize();

		safeLocalStorage.setItem(this.getLocalStorageKey(name, environmentId, userId), value);
	}

	public async clear(name: string): Promise<void> {
		const {
			currentUserIdentity: {
				userId
			},
			databaseConfiguration: {
				environmentId
			}
		} = await waitForUserConfigurationToInitialize();

		safeLocalStorage.removeItem(this.getLocalStorageKey(name, environmentId, userId));
	}

	private getLocalStorageKey(name: string, environmentId: Guid, userId: number): string {
		return `zustandStores:${name}:${environmentId}:${userId}`;
	}
}